import { configure } from "@amzn/sentry-fetch";
import { Alert } from "@amzn/storm-ui";
import FromHtml from "@amzn/storm-ui-from-html";
import { useAsync } from "@react-hookz/web";
import React, { useEffect } from "react";
import type { ComponentProps } from "react";
import styled from "styled-components";

import { getAuthSettings } from "../api/websiteSettings";

const sentryFetch = configure({
    fetchFunction: window.fetch,
    followMidwayStepUp: true,
});

interface IsItDownBannerProps {
    readonly level: number;
    readonly message: string;
}

interface IsItDownData {
    message?: string | undefined;
    level?: number | undefined;
}

interface IsItDownResponse {
    components: {
        component: {
            status: string;
            indicator: number;
            override_status?: string;
            override_indicator?: number;
        };
    };
}

const BannerWrapper = styled.div`
    margin-bottom: 1em;
`;

export function IsItDownBanner(): JSX.Element | null {
    const dashboardId = useIsItDownDashboardId();
    const [state, actions] = useAsync(async () => {
        if (!dashboardId) {
            return parseIsItDownResponse();
        }
        const res = await sentryFetch(
            `https://is-it-down.amazon.com/${dashboardId}/components/status.json`,
        );
        const json: IsItDownResponse | undefined = await res.json();
        return parseIsItDownResponse(json);
    });
    useEffect(() => {
        void actions.execute();
    }, [actions, dashboardId]);

    if (state.status !== "success") {
        return null;
    }
    if (!state.result || !state.result.level || !state.result.message) {
        return null;
    }

    const alertType = getAlertType(state.result.level);
    if (!alertType) {
        return null;
    }
    return (
        <IsItDownBannerWidget
            level={state.result.level}
            message={state.result.message}
        />
    );
}

/**
 * Is It Down banner config for InsightsHub pages.
 * https://is-it-down.amazon.com/dashboard_groups/InsightsHub
 */
function useIsItDownDashboardId(): string | null {
    const { stage } = getAuthSettings();
    if (!["alpha", "beta", "gamma", "prod"].includes(stage)) {
        return "ihub-alpha";
    }
    return `ihub-${stage}`;
}

export function IsItDownBannerWidget({
    level,
    message,
}: IsItDownBannerProps): JSX.Element | null {
    const [open, setOpen] = React.useState<boolean>(true);

    const alertType = getAlertType(level);
    if (!alertType) {
        return null;
    }
    return (
        <BannerWrapper>
            <Alert
                variant={alertType}
                withCloseButton={alertType !== "error"}
                isOpen={open}
                onClose={() => setOpen(false)}
            >
                <FromHtml
                    // https://is-it-down.amazon.com sanitizes the html
                    // doesnt allow target as an attribute. This is hacky but needed
                    html={message.replace(/href/g, "target='_blank' href")}
                />
            </Alert>
        </BannerWrapper>
    );
}

function parseIsItDownResponse(json?: IsItDownResponse): IsItDownData {
    const component = json?.components?.component;

    const message = component?.override_status ?? component?.status;
    const level = component?.override_indicator ?? component?.indicator;

    return { message, level };
}

function getAlertType(
    level: number | undefined,
): ComponentProps<typeof Alert>["variant"] | null {
    switch (level) {
        case 1:
            return "info";
        case 2:
            return "warning";
        case 3:
            return "error";
        default:
            return null;
    }
}
